<script setup>
import { onMounted } from "vue";
import { RouterLink } from "vue-router";
import { useCookieConsent } from "@/composables/useCookieConsent";
import { useEventAnnouncement } from "@/composables/useEventAnnouncement";

const { checkConsent } = useCookieConsent();
const { isOpen, closePopup } = useEventAnnouncement();

onMounted(() => {
	checkConsent();
});
</script>

<template>
	<Teleport to="body">
		<Transition name="event-announcement">
			<aside
				v-if="isOpen"
				class="event-announcement"
				role="dialog"
				aria-labelledby="event-announcement-title"
			>
				<button
					class="event-announcement__close"
					type="button"
					aria-label="Закрыть"
					@click="closePopup"
				>
					<svg width="24" height="24" aria-hidden="true">
						<use href="#x"></use>
					</svg>
				</button>

				<div class="event-announcement__container">
					<div class="event-announcement__content">
						<h2 id="event-announcement-title" class="event-announcement__title">
							Успейте поймать атмосферу лета.
						</h2>
						<div class="event-announcement__content__block">

							<p class="event-announcement__period">Только с 12 июня по 30 сентября:</p>

							<ul class="event-announcement__list">
								<li class="event-announcement__list-item">☕ Попробуйте чайные напитки</li>
								<li class="event-announcement__list-item">✨ Получите чайное предсказание</li>
								<li class="event-announcement__list-item">🌿 Насладитесь атмосферой лета у воды</li>
							</ul>
						</div>

						<RouterLink
							to="/parkgorkogo/"
							class="event-announcement__btn btn"
							@click="closePopup"
						>
							Подробнее
						</RouterLink>


					</div>
				</div>
			</aside>
		</Transition>
	</Teleport>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.event-announcement {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 4;
	border-left: 1px solid var(--border-accent-2-3);
	border-right: 1px solid var(--border-accent-2-3);
	border-top: 1px solid var(--border-accent-2-3);
	border-bottom: none;
	border-radius: 4rem 4rem 0 0;
	padding: 4.8rem 2rem 4.8rem;
	backdrop-filter: blur(44.70000076293945px);
	box-shadow: inset 0 13px 35px 0 rgba(255, 255, 255, 0.14);
	background: linear-gradient(143deg, #165230 0%, #0b2e1b 100%);

	@media (max-width: 992px) {
		border-radius: 2.4rem 2.4rem 0 0;
		padding: 4rem 1.5rem;
	}

	&__close {
		border: 1px solid var(--border-default-1);
		border-radius: 1.6rem;
		width: 4.8rem;
		height: 4.8rem;
		position: absolute;
		top: 2.3rem;
		right: 2.3rem;
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--text-inverse-100);
		background: transparent;
		transition: background-color var(--transition);

		@media (hover: hover) {
			&:hover {
				background-color: #fff;

				svg {
					stroke: #0D2E1A;
				}
			}
		}

		svg {
			width: 3.2rem;
			height: 3.2rem;
			transition: all var(--transition);
		}

		@media (max-width: 992px) {
			top: 1.5rem;
			right: 1.5rem;
			width: 3.2rem;
			height: 3.2rem;
			border-radius: 1.2rem;

			svg {
				width: 2.4rem;
				height: 2.4rem;
			}
		}
	}

	&__container {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		width: 94rem;

		margin: 0 auto;

		@media (max-width: 992px) {
			width: 100%;
		}
	}

	&__content {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;

		&__block {
			width: calc(100% - 29rem);
		}

		@media (max-width: 992px) {
			flex-direction: column;

			&__block {
				width: 100%;
			}
		}
	}

	&__title {
		width: 100%;
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 4.8rem;
		line-height: 1.1;
		color: var(--text-inverse-100);
		display: flex;
		gap: 0.8rem;
		margin-bottom: 4rem;

		@media (max-width: 992px) {
			font-size: 2.5rem;
			margin-bottom: 2.4rem;
		}
	}

	&__period {
		font-family: var(--font-family);
		font-weight: 700;
		font-size: 2.4rem;
		line-height: 1.2;
		color: var(--text-accent-2-100);
		margin-bottom: 2rem;

		@media (max-width: 992px) {
			font-size: 1.8rem;
			margin-bottom: 2.4rem;
		}
	}

	&__list {
		display: flex;
		flex-direction: column;
		gap: 0.8rem;
		margin: 0;
		padding: 0;
		list-style: none;

		@media (max-width: 992px) {
			margin-bottom: 2.4rem;
		}
	}

	&__list-item {
		font-family: var(--font-family);
		font-weight: 500;
		font-size: 2rem;
		line-height: 1.2;
		color: var(--text-inverse-100);

		@media (max-width: 992px) {
			font-size: 1.6rem;
		}
	}

	&__btn {
		width: 29rem;
		flex-shrink: 0;

		@media (max-width: 992px) {
			width: 100%;
		}
	}
}

.event-announcement-enter-active,
.event-announcement-leave-active {
	transition: transform 0.45s ease;
}

.event-announcement-enter-from,
.event-announcement-leave-to {
	transform: translate(0, 100%);
}

.event-announcement-enter-to,
.event-announcement-leave-from {
	transform: translate(0, 0);
}
</style>
